import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event?.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event?.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      if (event.target.dataset.productId && this.dataset.productId) {
        return;
      }
    }

    const { priceContainer, volumePricingNote } = this.refs;
    if (!event?.detail?.data?.html) return;
    
    let newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) {
      newProductPrice = event.detail.data.html.querySelector('product-price') || event.detail.data.html.querySelector('.price');
    }
    
    if (!newProductPrice) return;

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]') || newProductPrice.querySelector('.price') || newProductPrice;
    
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
    if (!newNote) {
      if (volumePricingNote) volumePricingNote.remove();
    } else if (!volumePricingNote) {
      if (newPrice) {
        newPrice.insertAdjacentElement('afterend', newNote.cloneNode(true));
      }
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };
} // <-- THIS WAS MISSING! This cleanly seals the class.

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}